import { ThemeProvider } from "@mui/material/styles";
import Styles from "./styles";
import { BrowserRouter as Router, useNavigate } from "react-router-dom";
import AppRoutes from "./Routes";
import { UIProvider } from "./state/ui";
import Toast from "./components/ui/toast";
import './styles/react-date-picker.css';
import './App.css';
import { AppProvider } from "./state/appState";
import { QueryClient, QueryClientProvider } from "react-query";
import { LoadingProvider, useLoading } from "./contexts/loadingContext";
import { useEffect } from "react";
import { setLoadingHandlers, setCsrfTokenGetter, setNavigationHandler, setLogoutHandler } from "./service/httpService";
import GlobalSpinner from "./components/globalSpinner";
import { PreferencesProvider } from "hooks/usePreferences";
import { EncountersProvider } from './contexts/encountersContext';
import { AuthProvider } from './contexts/authContext';
import { LocationProvider } from './contexts/locationContext';
import { useAuth } from './hooks/useAuth';
import ChangePasswordDialog from './components/auth/ChangePasswordDialog';

const queryClient = new QueryClient()

// This new component will be responsible for initializing the global loading handlers.
// It MUST be rendered INSIDE the LoadingProvider.
function AppSetup() {
  const { showLoading, hideLoading } = useLoading(); // <--- This call is now correctly within the Provider's scope
  const { csrfToken, logout } = useAuth();
  const navigate = useNavigate();

  // console.log('Context value in AppSetup:', { showLoading, hideLoading }); // For debugging if needed

  useEffect(() => {
    setLoadingHandlers(showLoading, hideLoading);
  }, [showLoading, hideLoading]);

  useEffect(() => {
    setCsrfTokenGetter(() => csrfToken);
  }, [csrfToken]);

  useEffect(() => {
    setNavigationHandler(navigate);
  }, [navigate]);

  useEffect(() => {
    setLogoutHandler(logout);
  }, [logout]);

  // This component doesn't render anything itself; it just sets up side effects.
  return null;
}

// Component to handle password change requirement
function PasswordChangeHandler() {
  const { mustChangePassword, onPasswordChanged } = useAuth();
  
  return (
    <ChangePasswordDialog
      open={mustChangePassword}
      onSuccess={onPasswordChanged}
      forced={true}
    />
  );
}

function App() {

  return (
    // 1. LoadingProvider must be at the top to wrap all consumers
    <LoadingProvider>
      {/* 2. QueryClientProvider is often next for data fetching */}
      <QueryClientProvider client={queryClient}>
        <EncountersProvider>
          {/* 3. AppProvider for your general app state */}
          <AppProvider>
            {/* 4. ThemeProvider for MUI styling */}
            <ThemeProvider theme={Styles.theme}>
              {/* 5. Router for navigation */}
              <Router basename={process.env.REACT_APP_BASENAME}>
                {/* 6. AuthProvider MUST come before PreferencesProvider */}
                <AuthProvider>
                  {/* 6.5. LocationProvider for global location selection */}
                  <LocationProvider>
                    {/* 7. PreferencesProvider needs user from AuthProvider */}
                    <PreferencesProvider>
                      {/* 8. UIProvider for UI-specific state */}
                      <UIProvider>
                        {/** AppSetup sets loading handlers */}
                        <AppSetup />
                        {/** PasswordChangeHandler shows dialog when mustChangePassword is true */}
                        <PasswordChangeHandler />
                        {/* Wrap protected routes in RequireAuth inside AppRoutes implementation or here */}
                        <AppRoutes />
                        <Toast />
                        <GlobalSpinner />
                      </UIProvider>
                    </PreferencesProvider>
                  </LocationProvider>
                </AuthProvider>
              </Router>
            </ThemeProvider>
          </AppProvider>
        </EncountersProvider>
      </QueryClientProvider>
    </LoadingProvider>

  );
}

export default App;
